// @flow

import DOM from '../../util/dom';
import {extend, bindAll} from '../../util/util';
import WeatherAlertFilterControl from './weatheralert_filter_control';
import {hexToRgb} from '../../util/color';
import {Event} from '../../util/evented';
import {ContentVisibilityEvent, ContentRemoveEvent} from '../events';

type Options = {
    id: string
};

const defaultOptions: Options = {
    id: 'weatheralertslegend'
};

/**
 * The `WeatherAlertLegendControl` adds the ability to show a legend for the weather alert layer.
 *
 * @implements {IControl}
 *
 * @example
 * var weatherAlertLegendControl = new TrimbleMaps.WeatherAlertLegendControl();
 * map.addControl(weatherAlertLegendControl, 'top-left');
 */
class WeatherAlertLegendControl {
    _map: any;
    _options: Options;
    _container: HTMLElement;
    _listContainer: HTMLElement;
    _visibleTypes: Array<string>;
    _enabledTypes: Object;

    constructor(options: Options) {
        this._options = extend({}, defaultOptions, options);

        this._enabledTypes = new Map();

        bindAll([
            '_onLoad',
            '_onSourceData',
            '_onSourceDataLoading',
            '_refreshAlertList',
            '_onChange',
            '_filterFeatures',
            '_getBaseFilter',
            '_contentLayerVisibilityChanged',
            '_contentLayerRemoved',
        ], this);
    }

    onAdd(map: any) {
        this._map = map;
        this._container = DOM.create('div', 'trimblemaps-ctrl trimblemaps-ctrl-accordion-content');
        this._container.id = 'weatherlegendcontainer';
        this._listContainer = DOM.create('div', 'trimblemaps-ctrl-walist');
        const hr = DOM.create('hr', 'trimblemaps-ctrl-hr');
        this._container.appendChild(hr);
        this._container.appendChild(this._listContainer);
        this._container.style.display = 'none';
        this._map.on('load', this._onLoad);

        // if layer is already loaded, match the visibility
        if (this._map.getLayer('weatheralert-weatheralert_fill')) {
            const layerVisibility = this._map.getLayoutProperty('weatheralert-weatheralert_fill', 'visibility');
            this._container.style.display = (layerVisibility === 'visible') ? 'block' : 'none';
            this._map.on('sourcedata', this._onSourceData);
            this._refreshAlertList();
        }

        this._map.on('content.visibility', this._contentLayerVisibilityChanged);
        this._map.on('content.remove', this._contentLayerRemoved);
        return this._container;
    }

    onRemove() {
        this._map.off('sourcedata', this._onSourceData);
        this._map.off('load', this._onLoad);

        this._map.off('content.visibility', this._contentLayerVisibilityChanged);
        this._map.off('content.remove', this._contentLayerRemoved);

        DOM.remove(this._listContainer);
        DOM.remove(this._container);
        delete this._map;
    }
    _onLoad() {
        this._map.on('sourcedata', this._onSourceData);
    }

    _onSourceData(e: any) {
        if ((e.sourceId === 'WeatherAlerts') && (!e.sourceDataType)) {
            this._refreshAlertList();
            this._container.style.display = 'block';
        }
    }

    _refreshAlertList() {
        const features = this._map.querySourceFeatures('WeatherAlerts', {sourceLayer: 'weather_alert', filter: this._getBaseFilter()});
        this._visibleTypes = features
            .map(feature => feature.properties.eventName)
            .filter((e, i, a) => i === a.indexOf(e));

        this._visibleTypes.forEach(feature => {
            if (!this._enabledTypes.has(feature)) {
                this._enabledTypes.set(feature, true);
            }
        });

        this._listContainer.innerHTML = '';

        if (this._visibleTypes.length > 0) {
            this._visibleTypes.forEach((e) => {
                const feature = features.find(feature => feature.properties.eventName === e);
                const props = feature.properties;
                const rgbC = hexToRgb(props.color);
                const span = DOM.create('span');
                span.innerHTML = e;

                const input = DOM.create('input');
                input.setAttribute('type', 'checkbox');
                input.style.height = '16px';
                input.style.width = '16px';
                input.checked = (this._enabledTypes.get(e));
                input.value = e;
                input.addEventListener('change', this._onChange);

                const label = DOM.create('label');
                label.appendChild(input);
                label.appendChild(span);
                const div = DOM.create('div', 'trimblemaps-ctrl-wafilter');
                div.appendChild(label);
                const box = DOM.create('div');
                box.style.borderWidth = '1px';
                box.style.borderStyle = 'solid';
                if (rgbC !== null) {
                    box.style.borderColor = `rgba(${rgbC.r},${rgbC.g},${rgbC.b},0.7)`;
                    box.style.backgroundColor = `rgba(${rgbC.r},${rgbC.g},${rgbC.b},0.2)`;
                }
                box.style.float = 'right';
                box.style.height = '20px';
                box.style.width = '20px';
                div.appendChild(box);
                this._listContainer.appendChild(div);
            });
        } else {
            const div = DOM.create('div');
            div.style.margin = '6px';
            div.innerHTML = 'No Alerts';
            this._listContainer.appendChild(div);
        }

        this._filterFeatures();

    }

    _getEnabledTypes (): Array<string> {
        const filter = [];

        this._enabledTypes.forEach((value, key) => {
            if (value) filter.push(key);
        });
        return filter;
    }

    _onChange(e: any) {
        this._enabledTypes.set(e.target.value, e.target.checked);
        this._filterFeatures();
        /**
         * Fired when weather alert event name is changed.
         *
         * @event weatheralert.eventname
         * @memberof Map
         * @instance
         * @type {Object}
         * @property {Event} object with control Id and selected key value pairs have been returned.
         */
        this._map.fire(new Event(
            'weatheralert.eventname',
            {
                id: this._options.id,
                data: this._getEnabledTypes()
            }
        ));
    }

    _filterFeatures() {
        const filter = this._getEnabledTypes();
        const newFilter = this._getBaseFilter();
        newFilter.push(['in', 'eventName', ...filter]);
        if (this._map.getLayer('weatheralert-weatheralert_outline')) {
            this._map.setFilter('weatheralert-weatheralert_fill', newFilter);
            this._map.setFilter('weatheralert-weatheralert_outline', newFilter);
        }
    }

    _getBaseFilter() {
        const newFilter = ['all'];

        this._map._controls.forEach((control: any) => {
            if (control instanceof WeatherAlertFilterControl) {
                newFilter.push(...control.getFilter());
            }
        });
        return newFilter;
    }

    _contentLayerVisibilityChanged(evt: ContentVisibilityEvent) {
        if (evt.id === 'weatheralert') {
            this._container.style.display = (evt.isVisible) ? 'block' : 'none';
        }
    }

    _contentLayerRemoved(evt: ContentRemoveEvent) {

        if (evt.id === 'weatheralert') {
            this._container.style.display = 'none';
        }
    }
}

export default WeatherAlertLegendControl;
